import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";

/** A stretch of the shift with no route on it, long enough to record one. */
export interface ShiftFreeSlot {
  start: Date;
  end: Date;
  /** Whole minutes, for labelling. */
  minutes: number;
}

/** The backend rejects a pseudo-route shorter than this, so shorter gaps are not offered. */
export const MINIMUM_SLOT_MINUTES = 30;

/** Keeps a recorded route from touching the ones it sits between. */
const NEIGHBOUR_BUFFER_MINUTES = 5;

/** Shifts without a recorded end are treated as a standard eight hours. */
const DEFAULT_SHIFT_HOURS = 8;

const MINUTE = 60 * 1000;

const at = (timestamp?: ConnectProto.Job["twStartAt"]) => {
  const date = fromTimestamp(timestamp);
  return date && !isNaN(date.getTime()) ? date : undefined;
};

/** A route occupies the time it actually took, falling back to what was planned. */
const routeBounds = (route: ConnectProto.Route) => {
  const start = at(route.factStartAt) ?? at(route.planStartAt);
  const end = at(route.factEndAt) ?? at(route.planEndAt) ?? start;
  return start && end && end.getTime() >= start.getTime() ? { start, end } : undefined;
};

const shiftBounds = (shift: ConnectProto.DriverOnShift) => {
  const start = at(shift.startedAt) ?? at(shift.twStartAt) ?? at(shift.createdAt);
  if (!start) return undefined;
  const end =
    at(shift.completedAt) ??
    at(shift.twEndAt) ??
    new Date(start.getTime() + DEFAULT_SHIFT_HOURS * 60 * MINUTE);
  return end.getTime() > start.getTime() ? { start, end } : undefined;
};

/**
 * Whole minutes, pulled inwards. A slot's bounds are shown and typed back as
 * "HH:mm", so one starting mid-minute would flag its own auto-filled start as
 * lying before itself. Rounding in rather than out also keeps the slot clear of
 * the routes it sits between.
 */
const ceilToMinute = (date: Date) => new Date(Math.ceil(date.getTime() / MINUTE) * MINUTE);
const floorToMinute = (date: Date) => new Date(Math.floor(date.getTime() / MINUTE) * MINUTE);

const toSlot = (rawStart: Date, rawEnd: Date, minimumMinutes: number): ShiftFreeSlot | undefined => {
  const start = ceilToMinute(rawStart);
  const end = floorToMinute(rawEnd);
  const minutes = Math.floor((end.getTime() - start.getTime()) / MINUTE);
  return minutes >= minimumMinutes ? { start, end, minutes } : undefined;
};

interface ComputeShiftFreeSlotsArgs {
  shift?: ConnectProto.DriverOnShift;
  /** The shift's routes, in any order. */
  routes: ConnectProto.Route[];
  minimumMinutes?: number;
}

/**
 * The stretches of a shift a pseudo-route can be recorded into: before the first
 * route, between consecutive ones, and after the last. Cancelled routes free
 * their time up; every other route blocks it. Each slot is pulled in by a few
 * minutes at the ends it shares with a route, so a recorded trip never starts
 * the instant another one finished.
 */
export const computeShiftFreeSlots = ({
  shift,
  routes,
  minimumMinutes = MINIMUM_SLOT_MINUTES,
}: ComputeShiftFreeSlotsArgs): ShiftFreeSlot[] => {
  if (!shift) return [];
  const bounds = shiftBounds(shift);
  if (!bounds) return [];

  const occupied = routes
    .filter((route) => route.status !== ConnectProto.RouteStatus.CANCELLED)
    .flatMap((route) => routeBounds(route) ?? [])
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  if (occupied.length === 0) {
    return [toSlot(bounds.start, bounds.end, minimumMinutes) ?? []].flat();
  }

  const buffer = NEIGHBOUR_BUFFER_MINUTES * MINUTE;
  const slots: ShiftFreeSlot[] = [];
  // Overlapping routes would otherwise produce a negative gap; the cursor only
  // ever moves forward, so a route inside another one simply adds nothing.
  let cursor = bounds.start;

  occupied.forEach(({ start, end }) => {
    if (start.getTime() > cursor.getTime()) {
      const slotStart =
        cursor.getTime() === bounds.start.getTime() ? cursor : new Date(cursor.getTime() + buffer);
      const slot = toSlot(slotStart, new Date(start.getTime() - buffer), minimumMinutes);
      if (slot) slots.push(slot);
    }
    if (end.getTime() > cursor.getTime()) cursor = end;
  });

  if (bounds.end.getTime() > cursor.getTime()) {
    const slot = toSlot(new Date(cursor.getTime() + buffer), bounds.end, minimumMinutes);
    if (slot) slots.push(slot);
  }

  return slots;
};

/** "HH:mm" in the viewer's timezone, the shape a native time input wants. */
export const toTimeInput = (date: Date) =>
  `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;

/**
 * Marks the rollover with the caller's translated suffix, so a night slot does
 * not read as a backwards range.
 */
export const formatSlotLabel = (slot: ShiftFreeSlot, nextDaySuffix: string) => {
  const crossesMidnight = slot.start.toDateString() !== slot.end.toDateString();
  return `${toTimeInput(slot.start)} - ${toTimeInput(slot.end)}${crossesMidnight ? ` ${nextDaySuffix}` : ""}`;
};

/** Puts an "HH:mm" back on a given day. */
const withTimeOfDay = (day: Date, time: string) => {
  const [hours, minutes] = time.split(":").map(Number);
  const result = new Date(day);
  result.setHours(hours ?? 0, minutes ?? 0, 0, 0);
  return result;
};

/** Formats what the operator is typing; only the finished value has to be valid. */
export const normalizeTimeInput = (raw: string) => {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

const parseTimeInput = (time: string) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time);
  if (!match) return undefined;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours <= 23 && minutes <= 59 ? { hours, minutes } : undefined;
};

/** How far outside the slot an instant falls; zero while it is inside. */
const distanceOutside = (date: Date, slot: ShiftFreeSlot) =>
  Math.max(slot.start.getTime() - date.getTime(), date.getTime() - slot.end.getTime(), 0);

/**
 * Puts an "HH:mm" on whichever of the slot's days keeps it inside the slot. A
 * night shift's slot runs past midnight, so "01:30" in a 22:00 - 03:00 slot
 * belongs to the day the slot ends on, not the one it starts on.
 */
export const resolveTimeInSlot = (slot: ShiftFreeSlot, time: string): Date => {
  const candidates = [withTimeOfDay(slot.start, time), withTimeOfDay(slot.end, time)];
  const inside = candidates.find((date) => distanceOutside(date, slot) === 0);
  if (inside) return inside;

  const nearest = candidates.reduce((best, date) =>
    distanceOutside(date, slot) < distanceOutside(best, slot) ? date : best,
  );
  const clamped = Math.min(Math.max(nearest.getTime(), slot.start.getTime()), slot.end.getTime());
  return new Date(clamped);
};

/**
 * Whether a typed time lands inside the slot on either of the days it spans.
 * Out-of-range values are kept and flagged rather than snapped back, so the
 * operator sees what they typed.
 */
export const isTimeInSlot = (slot: ShiftFreeSlot, time: string) =>
  Boolean(parseTimeInput(time)) &&
  [withTimeOfDay(slot.start, time), withTimeOfDay(slot.end, time)].some(
    (date) => distanceOutside(date, slot) === 0,
  );
